import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Heart, ShoppingBag, Store, Menu, LogOut, User, Package, LayoutDashboard, X, Gavel, Languages, Bell, ShieldCheck, UserCircle, Coins, RefreshCw, Home } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useCart } from '../lib/cart';
import { useI18n } from '../lib/i18n';
import AuthModal from './AuthModal';
import NotificationsBell from './NotificationsBell';
import PaymentBadges from './PaymentBadges';
import EssentialsBar from './EssentialsBar';
import EmergencySosButton from './EmergencySosButton';
import ReviewReminderBanner from './ReviewReminderBanner';
import SecurityLoginAlertBanner from './SecurityLoginAlertBanner';
import { Toaster } from 'sonner';
import BottomNav from './BottomNav';
import VoiceCartDialog from './VoiceCartDialog';
import LiveClock from './LiveClock';
import OfflineBar from './OfflineBar';
import LiveDomainBanner from './LiveDomainBanner';
import EmailVerifyBanner from './EmailVerifyBanner';
import KhataReceiptsDialog from './KhataReceiptsDialog';
import { useCompany } from '../lib/company';
import { isBizStaff } from '../lib/auth';

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { totals } = useCart();
  const { lang, setLang, t } = useI18n();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const openAuth = (mode) => { setAuthMode(mode); setAuthOpen(true); };

  useEffect(() => {
    const handler = (e) => openAuth((e.detail && e.detail.mode) || 'signup');
    window.addEventListener('mandispot:open-auth', handler);
    return () => window.removeEventListener('mandispot:open-auth', handler);
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const nav = useNavigate();
  const { pathname } = useLocation();

  if (pathname.startsWith('/business')) {
    return (
      <div className="min-h-screen bg-sand" data-testid="bizdesk-shell">
        {children}
        <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
        <Toaster position="top-center" richColors />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-sand pb-16 md:pb-0">
      <OfflineBar/>
      <LiveDomainBanner/>
      <EmailVerifyBanner/>
      <header data-testid="site-header" className="sticky top-0 z-40 backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5">
            <span className="relative inline-grid place-items-center w-11 h-11 rounded-full bg-[#FFF3E0] overflow-hidden border-2 border-[#E67E22] shadow-sm shrink-0">
              <img src="/family-hero.png" alt="BharatCart family with cart" className="w-full h-full object-cover scale-110" data-testid="brand-family-icon"/>
              <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-[#2E7D32] border-2 border-[#FDFBF7]"/>
            </span>
            <span className="flex flex-col leading-none">
              <span className="brand-logo text-2xl text-ink">BharatCart</span>
              <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D35400] mt-0.5 whitespace-nowrap">India's Marketplace</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-3 text-sm font-medium text-ink/80 whitespace-nowrap">
            <Link to="/catalog" data-testid="nav-catalog" className="hover:text-[#D35400]">Shop India</Link>
            <Link to="/brands" data-testid="nav-brands" className="hover:text-[#D35400]">Brands</Link>
            <Link to="/manufacturers" data-testid="nav-manufacturers" className={`${user ? "hidden 2xl:inline" : "hidden xl:inline"} hover:text-[#D35400]`}>Makers</Link>
            <Link to="/stores" data-testid="nav-stores" className="hover:text-[#D35400]">Local Shops</Link>
            <Link to="/categories" data-testid="nav-categories" className={`${user ? "hidden 2xl:inline" : "hidden xl:inline"} hover:text-[#D35400]`}>Categories</Link>
            <Link to="/tender/new" data-testid="nav-tender" title={t('request_quote')} className={`${user ? "hidden" : "hidden xl:inline-flex"} items-center gap-1 hover:text-[#D35400]`}><Gavel className="w-3.5 h-3.5"/>Quote</Link>
            <Link to="/become-seller" data-testid="nav-become-seller" className="hover:text-[#D35400]">Sell</Link>
          </nav>

          <div className="flex items-center gap-1.5">
            <Link to="/" data-testid="home-icon-button" title="Home" aria-label="Home" className="hidden 2xl:inline-flex items-center justify-center w-10 h-10 rounded-full border border-border bg-white hover:border-[#E67E22] hover:text-[#D35400] transition">
              <Home className="w-4 h-4" />
            </Link>
            <button
              data-testid="refresh-button"
              onClick={() => window.location.reload()}
              title="Refresh page"
              aria-label="Refresh page"
              className="hidden 2xl:inline-flex items-center justify-center w-10 h-10 rounded-full border border-border bg-white hover:border-[#E67E22] hover:text-[#D35400] hover:rotate-180 transition-all duration-500"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button data-testid="lang-toggle" onClick={() => setLang(lang === 'en' ? 'hi' : 'en')} title="Toggle language" className="hidden md:inline-flex items-center gap-1 rounded-full border border-border bg-white px-3 h-10 text-xs font-semibold hover:border-[#E67E22] hover:text-[#D35400] transition">
              <Languages className="w-3.5 h-3.5"/>{lang === 'en' ? 'हिं' : 'EN'}
            </button>
            <Link to="/wishlist" data-testid="wishlist-button" title="Wishlist" className="hidden md:inline-flex items-center justify-center w-10 h-10 rounded-xl border border-border bg-white hover:border-[#E67E22] hover:text-[#D35400] transition"><Heart className="w-4 h-4"/></Link>
            <Link to="/cart" data-testid="cart-button" className="relative inline-flex flex-col items-center justify-center min-w-[44px] h-10 px-1 rounded-xl border border-border bg-white hover:border-[#E67E22] hover:text-[#D35400] transition group">
              <div className="relative inline-flex items-center justify-center">
                <ShoppingBag className="w-4 h-4" />
                {totals.allCount > 0 && (
                  <span data-testid="cart-count" className="absolute -top-1.5 -right-2 bg-[#E67E22] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] grid place-items-center px-1">{totals.allCount}</span>
                )}
              </div>
              <span data-testid="cart-label" className="block text-[9px] font-bold uppercase tracking-[0.16em] leading-none mt-0.5">Cart</span>
            </Link>
            {user ? (
              <div className="hidden md:flex items-center gap-1">
                <NotificationsBell/>
                <Link to="/tenders" data-testid="my-tenders-link" title="Quotes" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs"><Gavel className="w-3.5 h-3.5"/><span className="sr-only">Quotes</span></Link>
                <Link to="/orders" data-testid="my-orders-link" title="My orders" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs"><Package className="w-3.5 h-3.5"/><span className="sr-only">{t('orders')}</span></Link>
                {user.role === 'customer' && (
                  <Link to="/price-alerts" data-testid="my-price-alerts-link" title="Price alerts" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs"><Bell className="w-3.5 h-3.5"/><span className="sr-only">Alerts</span></Link>
                )}
                <Link to="/coins" data-testid="coins-link" title="MandiCoins" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs !bg-gradient-to-r !from-[#FFF4E6] !to-[#FFD180] !border-[#F2A157] !text-[#8E3B00] hover:!brightness-95"><Coins className="w-3.5 h-3.5"/><span className="sr-only">Coins</span></Link>
                <Link to="/profile" data-testid="profile-link" title="Profile" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs"><UserCircle className="w-3.5 h-3.5"/><span className="sr-only">Profile</span></Link>
                {user.role === 'owner' && (
                  <Link to="/dashboard" data-testid="dashboard-link" title="Owner dashboard" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs"><LayoutDashboard className="w-3.5 h-3.5"/><span className="sr-only">{t('dashboard')}</span></Link>
                )}
                {user.role === 'admin' && (
                  <Link to="/admin" data-testid="admin-link" title="Admin" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs !bg-[#0D47A1] !text-white !border-[#0D47A1] hover:!bg-[#0a3578]"><ShieldCheck className="w-3.5 h-3.5"/><span className="sr-only">Admin</span></Link>
                )}
                {isBizStaff(user) && (
                  <Link to="/business" data-testid="business-link" title="BizDesk" className="btn-outline !p-0 w-10 h-10 !justify-center text-xs !bg-[#1B5E20] !text-white !border-[#1B5E20] hover:!bg-[#154a17]"><LayoutDashboard className="w-3.5 h-3.5"/><span className="sr-only">BizDesk</span></Link>
                )}
                <button data-testid="logout-button" title="Log out" onClick={() => { logout(); nav('/'); }} className="btn-outline !p-0 w-10 h-10 !justify-center text-xs"><LogOut className="w-3.5 h-3.5"/><span className="sr-only">{t('logout')}</span></button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <button data-testid="login-button" onClick={() => openAuth('signin')}
                  className="btn-outline whitespace-nowrap !py-2 !px-4 text-sm !bg-white !text-[#0D47A1] !border-[#0D47A1] hover:!bg-[#E3F2FD]">
                  <User className="w-4 h-4"/>{t('sign_in')}
                </button>
                <button data-testid="signup-button" onClick={() => openAuth('signup')}
                  className="btn-primary whitespace-nowrap !py-2 !px-4 text-sm">
                  <User className="w-4 h-4"/>Sign Up
                </button>
              </div>
            )}
            {!user && (
              <button data-testid="mobile-signup-button" onClick={() => openAuth('signup')}
                className="md:hidden btn-primary !py-2 !px-3 text-xs">Sign Up</button>
            )}
            <button data-testid="mobile-menu-toggle" className="md:hidden inline-flex w-10 h-10 items-center justify-center rounded-full border border-border bg-white" onClick={() => setMenuOpen(v => !v)}>
              {menuOpen ? <X className="w-4 h-4"/> : <Menu className="w-4 h-4"/>}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-border bg-[#FDFBF7] px-4 py-3 flex flex-col gap-2 text-sm">
            <Link to="/" onClick={() => setMenuOpen(false)} className="py-2 inline-flex items-center gap-2"><Home className="w-4 h-4"/>Home</Link>
            <Link to="/stores" onClick={() => setMenuOpen(false)} className="py-2">Local Shops</Link>
            <Link to="/catalog" onClick={() => setMenuOpen(false)} className="py-2" data-testid="mobile-nav-catalog">Shop all of India</Link>
            <Link to="/wishlist" onClick={() => setMenuOpen(false)} className="py-2" data-testid="mobile-nav-wishlist">My wishlist</Link>
            <Link to="/brands" onClick={() => setMenuOpen(false)} className="py-2" data-testid="mobile-nav-brands">Brands</Link>
            <Link to="/manufacturers" onClick={() => setMenuOpen(false)} className="py-2" data-testid="mobile-nav-manufacturers">Manufacturers</Link>
            <Link to="/partner/register" onClick={() => setMenuOpen(false)} className="py-2" data-testid="mobile-nav-partner">Register brand / manufacturer</Link>
            <Link to="/become-seller" onClick={() => setMenuOpen(false)} className="py-2">Sell on BharatCart</Link>
            {user && <Link to="/orders" onClick={() => setMenuOpen(false)} className="py-2">My Orders</Link>}
            {user?.role === 'owner' && <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="py-2">Dashboard</Link>}
            {!user && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <button data-testid="mobile-menu-signin" onClick={() => { setMenuOpen(false); openAuth('signin'); }}
                  className="btn-outline !py-2 !px-3 text-sm !text-[#0D47A1] !border-[#0D47A1]"><User className="w-4 h-4"/>Sign In</button>
                <button data-testid="mobile-menu-signup" onClick={() => { setMenuOpen(false); openAuth('signup'); }}
                  className="btn-primary !py-2 !px-3 text-sm"><User className="w-4 h-4"/>Sign Up</button>
              </div>
            )}
          </div>
        )}
      </header>

      {pathname !== '/' && <EssentialsBar/>}
      <SecurityLoginAlertBanner/>
      <ReviewReminderBanner/>

      <main className="flex-1">{children}</main>
      <EmergencySosButton/>

      <footer data-testid="site-footer" className="mt-12 border-t border-border bg-gradient-to-b from-white to-[#FBF8F1]">
        {/* Customer Care strip */}
        <div className="bg-[#1B1B1B] text-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 grid md:grid-cols-4 gap-5 text-sm">
            <div className="flex items-start gap-3" data-testid="footer-cc-phone">
              <span className="w-10 h-10 grid place-items-center rounded-full bg-[#E67E22] shrink-0">📞</span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Customer Care</div>
                <a href="tel:+914000000000" className="font-heading font-black text-lg leading-none hover:underline">+91-40-0000-0000</a>
                <div className="text-[11px] text-white/60 mt-1">Daily 7 AM – 10 PM IST · Telugu / Hindi / English</div>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="footer-cc-email">
              <span className="w-10 h-10 grid place-items-center rounded-full bg-[#0D47A1] shrink-0">✉️</span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Email support</div>
                <a href="mailto:care@bharatcart.org" className="font-semibold hover:underline">care@bharatcart.org</a>
                <div className="text-[11px] text-white/60 mt-1">Reply within 24 hours</div>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="footer-cc-grievance">
              <span className="w-10 h-10 grid place-items-center rounded-full bg-[#AD1457] shrink-0">⚖️</span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Grievance Officer (DPDP)</div>
                <a href="mailto:grievance@bharatcart.org" className="font-semibold hover:underline">grievance@bharatcart.org</a>
                <div className="text-[11px] text-white/60 mt-1">SLA: 30 days · As per DPDP Act, 2023</div>
              </div>
            </div>
            <div className="flex items-start gap-3" data-testid="footer-cc-emergency">
              <span className="w-10 h-10 grid place-items-center rounded-full bg-[#B71C1C] shrink-0">🚨</span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/60">Emergency / SOS</div>
                <a href="tel:112" className="font-heading font-black text-lg leading-none hover:underline">112</a>
                <div className="text-[11px] text-white/60 mt-1">Police · Fire · Medical (India)</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main link columns */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 grid md:grid-cols-5 gap-8 text-sm text-ink/70">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2">
              <span className="inline-grid place-items-center w-9 h-9 rounded-full bg-[#E67E22] text-white font-heading font-black text-sm">BC</span>
              <div className="brand-logo text-3xl text-ink leading-none">BharatCart</div>
            </div>
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#D35400] mt-2">India's Marketplace — Brands, Makers & Local Shops</div>
            <p className="mt-3 max-w-md leading-relaxed">
              India&apos;s trusted marketplace connecting manufacturers, brands, authorised distributors and sellers to customers — plus your neighbourhood local shops, delivered to your door.
            </p>
            <p className="mt-3 text-[11px] text-ink/50">Registered office · ADVN SOLUTIONS LLP, Hyderabad, Telangana 500001, India</p>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink mb-3">Help & support</div>
            <ul className="space-y-1.5">
              <li><Link to="/profile?tab=tickets" className="hover:text-[#D35400]">Raise a support ticket</Link></li>
              <li><Link to="/orders" className="hover:text-[#D35400]">Track my order</Link></li>
              <li><Link to="/profile?tab=privacy" className="hover:text-[#D35400]">Privacy & my data (DPDP)</Link></li>
              <li><Link to="/profile?tab=grievance" className="hover:text-[#D35400]">File a grievance</Link></li>
              <li><a href="mailto:care@bharatcart.org" className="hover:text-[#D35400]">Email care@bharatcart.org</a></li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink mb-3">Explore</div>
            <ul className="space-y-1.5">
              <li><Link to="/stores" className="hover:text-[#D35400]">All stores</Link></li>
              <li><Link to="/stores?category=vegetables" className="hover:text-[#D35400]">Vegetables</Link></li>
              <li><Link to="/stores?category=kirana" className="hover:text-[#D35400]">Kirana</Link></li>
              <li><Link to="/stores?category=mobiles" className="hover:text-[#D35400]">Mobiles</Link></li>
              <li><Link to="/stores?category=jewellery" className="hover:text-[#D35400]">Jewellery</Link></li>
              <li><Link to="/coins" className="hover:text-[#D35400]">MandiCoins rewards</Link></li>
            </ul>
          </div>

          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink mb-3">For sellers</div>
            <ul className="space-y-1.5">
              <li><Link to="/become-seller" className="hover:text-[#D35400]">Open a store</Link></li>
              <li><Link to="/partner/register?type=brand_owner" data-testid="footer-register-brand" className="hover:text-[#D35400]">Register your brand</Link></li>
              <li><Link to="/partner/register?type=manufacturer" data-testid="footer-register-manufacturer" className="hover:text-[#D35400]">Register as a manufacturer</Link></li>
              <li><Link to="/partner/register?type=distributor" className="hover:text-[#D35400]">Authorised distributors</Link></li>
              <li><Link to="/dashboard" className="hover:text-[#D35400]">Owner dashboard</Link></li>
              <li><Link to="/tenders" className="hover:text-[#D35400]">Group orders / tenders</Link></li>
            </ul>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-ink mt-5 mb-3">Legal & Policies</div>
            <ul className="space-y-1.5">
              <li><Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#D35400]">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#D35400]">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" data-testid="footer-refund-link" className="hover:text-[#D35400]">Refund &amp; Cancellation</Link></li>
              <li><Link to="/shipping-policy" data-testid="footer-shipping-link" className="hover:text-[#D35400]">Shipping &amp; Delivery</Link></li>
              <li><Link to="/contact" data-testid="footer-contact-link" className="hover:text-[#D35400]">Contact us</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-ink/60">Secure payments — accepted on BharatCart</div>
            <PaymentBadges size="sm"/>
          </div>
        </div>

        <div className="border-t border-border">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] text-ink/50">
            <div>© {new Date().getFullYear()} BharatCart · A marketplace by ADVN SOLUTIONS LLP. Jurisdiction: Hyderabad, Telangana.</div>
            <LiveClock testid="footer-live-clock" className="font-bold text-ink/60"/>
            <div className="flex items-center gap-3">
              <FooterGstin/>
              <span>·</span>
              <span>FSSAI: pending</span>
              <span>·</span>
              <span>Made in India 🇮🇳</span>
            </div>
          </div>
        </div>
      </footer>

      <BottomNav/>
      <VoiceCartDialog/>
      <KhataReceiptsDialog/>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
      <Toaster position="top-center" richColors />
    </div>
  );
}

function FooterGstin() {
  const c = useCompany();
  return <span data-testid="footer-gstin">{c?.legal_name || 'BharatCart'} · GSTIN: {c?.gstin || '—'}</span>;
}
